import { getProductSummary } from 'apiConnector/quote/summary';
import type { ProductSummaryMetadata } from 'components/common-form/sidePanel/docToolbar/summary/builder/model';
import type { ProductSummaryScheduleRow } from 'components/common-form/sidePanel/docToolbar/summary/builder/node/fields/Schedule/Schedule.model';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics/tabRemoved';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { CostAndCharge } from 'models/streaming/costsAndCharges';
import { ofType } from 'redux-observable';
import type { Observable } from 'rxjs';
import { NEVER } from 'rxjs';
import { catchError, map, mergeMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import type { IUnderlying } from 'shared/services/backends/underlyingBackend.service';
import type { BasketOptimizerResultRow } from 'state/advisory/basket-optimizer/model';
import { getResultRows } from 'state/advisory/basket-optimizer/selectors';
import { getFormEnrichedData } from 'state/formData/formData.selectors';
import { productSummaryReceived } from 'state/productSummaryDescription/productSummaryDescription.action';
import { getFormQuoteId, getRegulationInfos, getStaticWorkflowStatus } from 'state/selectors';
import type { EpicWithThunk } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { logFrontError } from 'utils/errors/logFrontError';

export interface EmailDescriptionApi {
  getProductSummary(summaryPostData: ProductSummaryPostData): Observable<ProductSummaryMetadata>;
}

type YesNo = 'Yes' | 'No';

interface IDealSplit {
  selectedcustodian?: string;
  notional?: number;
}

export interface ProductSummaryPostData {
  ClnCouponType: string;
  CouponDefType: string;
  ClnRecoverySettlement: string;
  DealSplit: IDealSplit[];
  IsSolved: boolean;
  KiBarrierType: string;
  ProductSubtype: string;
  ProductType: string;
  RemunerationMode: string;
  SelectedOnBehalfClient: ISalesCounterpart;
  SolvingMode: string;
  Underlying: IUnderlying;
  Underlyings: IUnderlying[];
  UnderlyingsCredit: IUnderlying[];
  UnderlyingsForex: IUnderlying[];
  UnderlyingsRate: IUnderlying[];
  Wrapper: string;
  KidRequired: boolean;
  CollateralName: string;
  ProductGovernanceRequired: boolean;
  IsCapped: YesNo;
  IsFloored: YesNo;
  CallPut: 'Call' | 'Put';
  HasPayUpsideOnRedemptionFeature: boolean;
  HasDefaultableUnderlyingFeature: boolean;
  OptimizedBasketResults: BasketOptimizerResultRow[];
  CostsAndChargesResults: CostAndCharge[] | undefined;
  Callability: string;
  SwapSchedule: ProductSummaryScheduleRow[];
  IsAutomaticPdiLeverage: YesNo;
  NotionalType: string;
  CouponStartPeriod?: number;
  IsBonusCouponActivated?: boolean;
  HasBarrier?: YesNo;
  CouponType?: string;
  PayoffType?: string;
  IsAutocallable?: boolean;

  HasSensitivity?: boolean;
  IsMagnet?: YesNo;
  IsMagnetMin?: YesNo;

  BasketType?: string;

  EnhancedBasketSize?: number;
  InitialFixingStrikeType?: string;
  Currency?: string;
  HasUpperCallStrike?: boolean;
  AveragingSchedule?: string;
  IsLowerLimitApplicable?: boolean;
  IsUpperLimitApplicable?: boolean;
  IsLowerLimit2Applicable?: boolean;
  IsUpperLimit2Applicable?: boolean;
  IsCallable?: boolean;
  IsInverseFloater?: YesNo;
  ZeroCouponType?: string;
}

function isSolved(state: AppState, tabId: string): boolean {
  try {
    const workflowStatus = getStaticWorkflowStatus(state, tabId);
    const result =
      workflowStatus === 'QUOTED' ||
      workflowStatus === 'QUOTED_TRADABLE' ||
      workflowStatus === 'QUOTED_PREVIOUSLY' ||
      workflowStatus === 'TRADING' ||
      workflowStatus === 'TRADED';

    return result;
  } catch (error) {
    logFrontError('Failed to get static workflow status {@FrontError}', error);
    return false;
  }
}

export function productSummaryEpic(): EpicWithThunk {
  return (actions$, state$, { httpClient }) =>
    actions$.pipe(
      ofType('PRODUCT_SUMMARY_REQUESTED'),
      withLatestFrom(state$),
      mergeMap(([{ tabId }, state]) => {
        const tabRemoved$ = tabRemoved(actions$, tabId, state$);

        const {
          ClnCouponType,
          CouponDefType,
          ClnRecoverySettlement,
          DealSplit,
          KiBarrierType,
          ProductSubtype,
          ProductType,
          RemunerationMode,
          SelectedOnBehalfClient,
          SolvingMode,
          Underlyings,
          UnderlyingsCredit,
          UnderlyingsForex,
          UnderlyingsRate,
          Wrapper,
          KidRequired,
          ProductGovernanceRequired,
          IsCapped,
          IsFloored,
          CallPut,
          HasPayUpsideOnRedemptionFeature,
          HasDefaultableUnderlyingFeature,
          Callability,
          SwapSchedule,
          IsAutomaticPdiLeverage,
          CollateralName,
          NotionalType,
          CouponStartPeriod,
          HasBarrier,
          CouponType,
          PayoffType,
          IsBonusCouponActivated,
          IsAutocallable,
          IsMagnet,
          IsMagnetMin,
          BasketType,
          EnhancedBasketSize,
          InitialFixingStrikeType,
          Currency,
          HasUpperCallStrike,
          AveragingSchedule,
          IsLowerLimitApplicable,
          IsUpperLimitApplicable,
          IsLowerLimit2Applicable,
          IsUpperLimit2Applicable,
          IsCallable,
          Underlying,
          IsInverseFloater,
          ZeroCouponType,
        } = getFormEnrichedData(state, tabId) as Readonly<ProductSummaryPostData>;

        const IsSolved = isSolved(state, tabId);
        const OptimizedBasketResults = getResultRows(state, tabId) || [];
        const quoteId = getFormQuoteId(state, 'static', tabId);
        const regulationInfos = getRegulationInfos(state, tabId, quoteId as string);
        const costsAndCharges = regulationInfos?.costAndCharge;

        const CostsAndChargesResults: CostAndCharge[] | undefined = costsAndCharges || [];

        return getProductSummary(httpClient)
          .getProductSummary({
            ClnCouponType,
            CouponDefType,
            ClnRecoverySettlement,
            DealSplit,
            IsSolved,
            KiBarrierType,
            ProductSubtype,
            ProductType,
            RemunerationMode,
            SelectedOnBehalfClient,
            SolvingMode,
            Underlying,
            Underlyings,
            UnderlyingsCredit,
            UnderlyingsForex,
            UnderlyingsRate,
            Wrapper,
            KidRequired,
            ProductGovernanceRequired,
            IsCapped,
            IsFloored,
            CallPut,
            OptimizedBasketResults,
            CostsAndChargesResults,
            HasPayUpsideOnRedemptionFeature,
            HasDefaultableUnderlyingFeature,
            Callability,
            SwapSchedule,
            IsAutomaticPdiLeverage,
            CollateralName,
            NotionalType,
            CouponStartPeriod,
            HasBarrier,
            CouponType,
            PayoffType,
            IsBonusCouponActivated,
            IsAutocallable,
            IsMagnet,
            IsMagnetMin,
            BasketType,
            EnhancedBasketSize,
            InitialFixingStrikeType,
            Currency,
            HasUpperCallStrike,
            AveragingSchedule,
            IsLowerLimitApplicable,
            IsUpperLimitApplicable,
            IsLowerLimit2Applicable,
            IsUpperLimit2Applicable,
            IsCallable,
            IsInverseFloater,
            ZeroCouponType,
          })
          .pipe(
            takeUntil(tabRemoved$),
            map(event => productSummaryReceived(tabId, event)),
            catchError(() => {
              return NEVER;
            }),
          );
      }),
    );
}
